/**
 * Capture a JPEG poster from a video File at ~1 second.
 *
 * Karim 2026-02-22: The news-feed cards currently show a black rectangle
 * until the tech taps a video — because iOS Safari won't paint the first
 * frame automatically. Uploading a captured poster alongside the video
 * lets us set `<video poster="…">` for an instant thumbnail.
 *
 * Runs entirely client-side (no ffmpeg). Falls back to `null` on any
 * failure — the caller must handle that gracefully.
 */

const POSTER_TIMEOUT_MS = 5000;
const TARGET_MAX_DIM = 720;

export async function capturePosterFromVideo(file, seekSec = 1) {
  if (!file || !(file.type || "").startsWith("video/")) return null;
  if (typeof document === "undefined") return null;

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.crossOrigin = "anonymous";
  video.src = url;

  const cleanup = () => URL.revokeObjectURL(url);

  try {
    // Wait for enough data to seek + paint.
    await withTimeout(
      new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = () => reject(new Error("Métadonnées vidéo illisibles"));
      }),
      POSTER_TIMEOUT_MS,
    );

    // Seek to ~1s but not past the end (poster of a short clip should still land).
    const target = Math.min(Math.max(0, seekSec), Math.max(0.05, (video.duration || seekSec) - 0.05));
    video.currentTime = target;
    await withTimeout(
      new Promise((resolve, reject) => {
        video.onseeked = resolve;
        video.onerror = () => reject(new Error("Seek impossible"));
      }),
      POSTER_TIMEOUT_MS,
    );

    // Downscale keeping aspect ratio for a small file.
    const ratio = video.videoWidth / video.videoHeight || 16 / 9;
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (Math.max(w, h) > TARGET_MAX_DIM) {
      if (w >= h) { w = TARGET_MAX_DIM; h = Math.round(w / ratio); }
      else { h = TARGET_MAX_DIM; w = Math.round(h * ratio); }
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas indisponible");
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.72));
    if (!blob) throw new Error("toBlob a échoué");
    const base = (file.name || "video").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}-poster.jpg`, { type: "image/jpeg" });
  } catch (err) {
    if (typeof console !== "undefined") console.debug("[capturePoster] fallback:", err?.message);
    return null;
  } finally {
    cleanup();
  }
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}
